"use client";

import { useCallback, useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import type { LandingPlan } from "@prisma/client";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { cf } from "@/components/brand/sfturnos-styles";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useConfirm } from "@/hooks/useConfirm";

function parseHighlights(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((x): x is string => typeof x === "string");
  return [];
}

export function LandingPlansEditor() {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const [plans, setPlans] = useState<LandingPlan[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const r = await fetchWithSessionSync("/api/admin/landing-plans", { credentials: "include" }, refresh);
    if (r.ok) setPlans(await r.json());
    setLoading(false);
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(id: string) {
    if (!(await confirm("¿Eliminar este plan de la web?"))) return;
    await fetchWithSessionSync(`/api/admin/landing-plans/${id}`, { method: "DELETE", credentials: "include" }, refresh);
    void load();
  }

  if (loading) return <LoadingFrame variant="light" compact minHeight={120} />;

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "8" })}>
      <NewPlanForm onCreated={() => void load()} />

      {plans.map((plan) => (
        <div key={plan.id} className={cx(cf.card, cf.cardPadding)}>
          <div className={css({ display: "flex", justifyContent: "space-between", gap: "4", flexWrap: "wrap", mb: "4" })}>
            <p className={css({ fontSize: "xs", fontWeight: "bold", color: "#6366f1", letterSpacing: "wider" })}>
              {plan.code}
            </p>
            <Button type="button" variant="danger" onClick={() => void remove(plan.id)}>
              Eliminar
            </Button>
          </div>
          <PlanEditForm plan={plan} onSaved={() => void load()} />
        </div>
      ))}
    </div>
  );
}

function NewPlanForm({ onCreated }: { onCreated: () => void }) {
  const { refresh } = useAuth();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await fetchWithSessionSync(
      "/api/admin/landing-plans",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: code.toUpperCase().replace(/[^A-Z0-9_]/g, "_"),
          name,
          priceLabel: "—",
          periodLabel: "/ mes",
          highlights: [],
          isPublished: false,
        }),
      },
      refresh,
    );
    setBusy(false);
    if (r.ok) {
      setCode("");
      setName("");
      onCreated();
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className={cx(cf.card, cf.cardPadding, css({ display: "flex", flexDir: "column", gap: "3" }))}>
      <p className={css({ fontWeight: "semibold", color: "#0f172a" })}>Nuevo plan (código interno único)</p>
      <p className={css({ fontSize: "xs", color: "#64748b", lineHeight: "1.4" })}>
        El código debe ser uno de <strong>FREE</strong>, <strong>PRO</strong> o <strong>ENTERPRISE</strong> para que coincida con el registro y el backend.
      </p>
      <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", sm: "1fr 1fr" }, gap: "3" })}>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Código (mayúsculas)</span>
          <input className={cf.input} value={code} onChange={(e) => setCode(e.target.value)} placeholder="EJ: TEAM" required />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Nombre visible</span>
          <input className={cf.input} value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
      </div>
      <Button type="submit" variant="primary" disabled={busy}>
        Crear borrador
      </Button>
    </form>
  );
}

function PlanEditForm({ plan, onSaved }: { plan: LandingPlan; onSaved: () => void }) {
  const { refresh } = useAuth();
  const [name, setName] = useState(plan.name);
  const [priceLabel, setPriceLabel] = useState(plan.priceLabel);
  const [periodLabel, setPeriodLabel] = useState(plan.periodLabel);
  const [description, setDescription] = useState(plan.description ?? "");
  const [highlightsText, setHighlightsText] = useState(parseHighlights(plan.highlights).join("\n"));
  const [isFeatured, setIsFeatured] = useState(plan.isFeatured);
  const [isPublished, setIsPublished] = useState(plan.isPublished);
  const [sortOrder, setSortOrder] = useState(String(plan.sortOrder));
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const highlights = highlightsText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    await fetchWithSessionSync(
      `/api/admin/landing-plans/${plan.id}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          priceLabel,
          periodLabel,
          description: description || null,
          highlights,
          isFeatured,
          isPublished,
          sortOrder: Number.parseInt(sortOrder, 10) || 0,
        }),
      },
      refresh,
    );
    setBusy(false);
    onSaved();
  }

  return (
    <form onSubmit={(e) => void save(e)} className={css({ display: "flex", flexDir: "column", gap: "4" })}>
      <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", md: "repeat(2, 1fr)" }, gap: "4" })}>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Nombre</span>
          <input className={cf.input} value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Orden</span>
          <input className={cf.input} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} inputMode="numeric" />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Precio (texto)</span>
          <input className={cf.input} value={priceLabel} onChange={(e) => setPriceLabel(e.target.value)} required />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Período (texto)</span>
          <input className={cf.input} value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} />
        </label>
      </div>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Descripción corta</span>
        <textarea
          className={cx(cf.input, css({ minH: "20", py: "3", resize: "vertical" }))}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
      </label>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Beneficios (uno por línea)</span>
        <textarea
          className={cx(cf.input, css({ minH: "28", py: "3", fontFamily: "inherit", resize: "vertical" }))}
          value={highlightsText}
          onChange={(e) => setHighlightsText(e.target.value)}
          rows={5}
        />
      </label>
      <label className={css({ display: "flex", alignItems: "center", gap: "2", cursor: "pointer" })}>
        <input type="checkbox" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} />
        <span className={css({ fontSize: "sm" })}>Destacar en la grilla</span>
      </label>
      <label className={css({ display: "flex", alignItems: "center", gap: "2", cursor: "pointer" })}>
        <input type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
        <span className={css({ fontSize: "sm" })}>Publicado en la web</span>
      </label>
      <Button type="submit" variant="primary" disabled={busy}>
        Guardar cambios
      </Button>
    </form>
  );
}
